import { useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { YAxisType } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { CalendarIcon } from '@/components/icons';
import { useWorldIndices } from '@/hooks/useWorldIndices';
import { useCommodities } from '@/hooks/useCommodities';
import { isCSEMarketOpen } from '@/utils/marketHours';
import type { Timeframe } from '@/types';

type PriceScaleType = 'normal' | 'log' | 'percentage';

const SCALE_OPTIONS: { value: PriceScaleType; label: string; title: string }[] = [
  { value: 'normal',     label: 'Reg.', title: 'Linear price axis' },
  { value: 'log',        label: 'Log',  title: 'Logarithmic price axis' },
  { value: 'percentage', label: 'Pct',  title: 'Percentage axis vs first visible bar close' },
];

const PERIODS = ['1D', '5D', '1M', '3M', '6M', 'YTD', '1Y', '5Y', 'All'] as const;
type Period = (typeof PERIODS)[number];

// Best-fit timeframe for each period so bars fill ~90% of the pane at a comfortable width.
// 1D  → 1m  intraday (~300 bars for a CSE session)
// 5D  → 5m  intraday (~300 bars over 5 sessions — backend keeps last 1000 1m bars ≈ 3.3 days,
//            so 5m gives ~200 bars which is still usable)
// 1M  → 1h  intraday (~110 bars — backend has ~3.3 days of 1m data so 1h gives only ~27 bars;
//            falls back to daily for accurate monthly view)
// 3M+ → daily/weekly/monthly
const PERIOD_TIMEFRAME: Record<Period, Timeframe> = {
  '1D':  '1m',
  '5D':  '5m',
  '1M':  '1D',
  '3M':  '1D',
  '6M':  '1D',
  'YTD': '1D',
  '1Y':  '1W',
  '5Y':  '1M',
  'All': '1W',
};

function periodToFromDate(period: Period): number | null {
  // Distinct from the Reset button's `null` (which just re-centers on the latest
  // data at the default zoom): 0 tells the chart effect to fit the ENTIRE loaded
  // history into the pane, the same way a period fits its own window.
  if (period === 'All') return 0;
  // Use UTC dates to match the backend's UTC-midnight timestamps (dateToUnix)
  const now = new Date();
  const y = now.getUTCFullYear(), mo = now.getUTCMonth(), d = now.getUTCDate();
  let ts: number;
  switch (period) {
    case '1D':  ts = Date.UTC(y, mo, d - 1);  break;
    case '5D':  ts = Date.UTC(y, mo, d - 5);  break;
    case '1M':  ts = Date.UTC(y, mo - 1, d);  break;
    case '3M':  ts = Date.UTC(y, mo - 3, d);  break;
    case '6M':  ts = Date.UTC(y, mo - 6, d);  break;
    case 'YTD': ts = Date.UTC(y, 0, 1);        break;
    case '1Y':  ts = Date.UTC(y - 1, mo, d);  break;
    case '5Y':  ts = Date.UTC(y - 5, mo, d);  break;
    default:    return null;
  }
  return Math.floor(ts / 1000);
}

export function PeriodBar() {
  const chartPeriod      = useChartStore((s) => s.chartPeriod);
  const setChartPeriod   = useChartStore((s) => s.setChartPeriod);
  const chartFromDate    = useChartStore((s) => s.chartFromDate);
  const setChartFromDate = useChartStore((s) => s.setChartFromDate);
  const setTimeframe     = useChartStore((s) => s.setTimeframe);
  const priceScaleType   = useChartStore((s) => s.priceScaleType);
  const setPriceScaleType = useChartStore((s) => s.setPriceScaleType);
  const chart            = useChartStore((s) => s.chart);
  const dateInputRef     = useRef<HTMLInputElement>(null);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [scalePos, setScalePos] = useState<{ bottom: number; right: number } | null>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const marqueeViewportRef = useRef<HTMLDivElement>(null);
  const marqueeContentRef = useRef<HTMLDivElement>(null);
  const { data: worldIndices, loading: worldLoading, error: worldError } = useWorldIndices();
  const [marqueeEnabled, setMarqueeEnabled] = useState(false);
  const [marqueeDistance, setMarqueeDistance] = useState(0);

  const { data: commodities, loading: commLoading, error: commError } = useCommodities();

  // Close scale dropdown when clicking outside (use 'click' so button onClick fires first)
  useEffect(() => {
    if (!scaleOpen) return;
    function onClick(e: MouseEvent) {
      if (scaleRef.current && !scaleRef.current.contains(e.target as Node)) {
        setScaleOpen(false);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [scaleOpen]);

  // Close dropdown on resize / orientation change
  useEffect(() => {
    if (!scaleOpen) return;
    function handleResize() { setScaleOpen(false); }
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [scaleOpen]);

  // Live IST clock — updates every second
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // IST = UTC+5:30
  const istMs  = now.getTime() + (5 * 60 + 30) * 60 * 1000;
  const istDate = new Date(istMs);
  const hh   = String(istDate.getUTCHours()).padStart(2, '0');
  const mm   = String(istDate.getUTCMinutes()).padStart(2, '0');
  const ss   = String(istDate.getUTCSeconds()).padStart(2, '0');
  const timeLabel = `${hh}:${mm}:${ss}`;
  const marketOpen = isCSEMarketOpen();

  function handlePeriodClick(period: Period) {
    setChartPeriod(period);
    setChartFromDate(periodToFromDate(period));
    setTimeframe(PERIOD_TIMEFRAME[period]);
  }

  function handleCustomDate(dateStr: string) {
    if (!dateStr) return;
    const ts = Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 1000);
    setChartPeriod(null);
    setChartFromDate(ts);
  }

  const isCustom        = chartPeriod === null && chartFromDate !== null;
  const customDateLabel = isCustom
    ? new Date(chartFromDate! * 1000).toISOString().slice(0, 10)
    : '';
  const todayISO = new Date().toISOString().slice(0, 10);
  const worldItems = worldIndices?.data ?? [];

  const worldTrackStyle = useMemo(() => {
    if (!marqueeEnabled || marqueeDistance <= 0) return undefined;
    const duration = Math.max(18, marqueeDistance / 22);
    return {
      ['--world-indices-distance' as string]: `${marqueeDistance}px`,
      ['--world-indices-duration' as string]: `${duration}s`,
    } as React.CSSProperties;
  }, [marqueeDistance, marqueeEnabled]);



  useEffect(() => {
    const viewport = marqueeViewportRef.current;
    const content = marqueeContentRef.current;
    if (!viewport || !content) return;

    const measure = () => {
      const overflow = content.scrollWidth > viewport.clientWidth + 8;
      setMarqueeEnabled(overflow);
      setMarqueeDistance(overflow ? content.scrollWidth : 0);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(content);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [worldItems.length, worldLoading, worldError]);



  return (
    <div
      className="glass-bar hidden md:flex items-center gap-1 px-2 h-11 shrink-0 border-t"
      style={{ borderTopColor: 'var(--glass-border)' }}
    >
      {/* Period pills — scrollable */}
      <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-none">
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => handlePeriodClick(p)}
            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors shrink-0 ${
              chartPeriod === p
                ? 'bg-accent/20 text-accent'
                : 'text-muted hover:text-text hover:bg-white/[0.06]'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="w-px h-4 mx-1 shrink-0" style={{ background: 'var(--glass-border)' }} />

      {/* Custom start-date picker — hidden native input triggered by button */}
      <div className="relative flex items-center shrink-0">
        <button
          onClick={() => dateInputRef.current?.showPicker?.()}
          title="Custom start date"
          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
            isCustom
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-white/[0.06]'
          }`}
        >
          <CalendarIcon size={12} />
          <span className="hidden md:inline">{isCustom ? customDateLabel : 'From'}</span>
        </button>
        <input
          ref={dateInputRef}
          type="date"
          min="2000-01-01"
          max={todayISO}
          value={customDateLabel}
          onChange={(e) => handleCustomDate(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          tabIndex={-1}
          aria-hidden
        />
      </div>

      {/* Reset — only visible when a period or custom date is active */}
      {(chartPeriod !== null || chartFromDate !== null) && (
        <button
          onClick={() => { setChartPeriod(null); setChartFromDate(null); setTimeframe('1D'); }}
          title="Reset to default view"
          className="px-2 py-0.5 rounded text-[11px] text-muted hover:text-text hover:bg-white/[0.06] transition-colors shrink-0"
        >
          ✕ <span className="hidden md:inline">Reset</span>
        </button>
      )}

      {/* Commodities — fixed-width uniform cells.
          data-nosnippet: confirmed via a live mobile SERP screenshot that
          Google was concatenating this strip's sibling <span> text (name +
          value + D/W % change, with no natural spacing between DOM nodes)
          into the search snippet instead of the actual meta description —
          e.g. "NIFTY5023,898. D +0.10%W -1.15%" for a Sri Lankan stock
          query. This attribute excludes the text from snippet generation
          without hiding it from users or affecting indexing otherwise. */}
      <div data-nosnippet className="hidden lg:flex items-center shrink-0 ml-2">
        {commLoading ? (
          <span className="text-[10px] text-muted px-3">Loading…</span>
        ) : commError ? (
          <span className="text-[10px] text-muted px-3">—</span>
        ) : (
          (commodities?.data ?? []).map((item, i) => (
            <div key={item.symbol} className="flex items-center">
              {i > 0 && (
                <div className="w-px h-5 mx-1 shrink-0" style={{ background: 'var(--glass-border)' }} />
              )}
              <div
                className="flex flex-col justify-center px-3 py-0.5 min-w-[120px]"
                title={`${item.label} · D ${formatPct(item.dayReturnPct)} · W ${formatPct(item.weekReturnPct)}`}
              >
                <div className="flex items-center justify-between text-[10px] leading-none">
                  <span className="text-muted">{item.label}</span>
                  <span className="font-medium text-text">{formatValue(item.value)}</span>
                </div>
                <div className="mt-0.5 flex items-center justify-between text-[9px] leading-none">
                  <span className={dayReturnClass(item.dayReturnPct)}>D {formatPct(item.dayReturnPct)}</span>
                  <span className={dayReturnClass(item.weekReturnPct)}>W {formatPct(item.weekReturnPct)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Spacer pushes everything after it to the right */}
      <div className="flex-1 hidden md:block" />

      {/* World indices strip — see data-nosnippet note on the Commodities
          strip above; same issue, same fix. Covers both the primary items
          and the aria-hidden marquee duplicate below, since aria-hidden
          only hints screen readers to skip content — it doesn't exclude
          text from search-engine snippet extraction the way data-nosnippet
          does. */}
      <div data-nosnippet ref={marqueeViewportRef} className="flex-1 min-w-[180px] max-w-[420px] overflow-hidden px-1">
        {worldLoading ? (
          <div className="flex h-full items-center text-[10px] text-muted">Loading world indices...</div>
        ) : worldError ? (
          <div className="flex h-full items-center text-[10px] text-muted">World indices unavailable</div>
        ) : (
          <div className={marqueeEnabled ? 'world-indices-marquee' : 'flex'} style={worldTrackStyle}>
            <div ref={marqueeContentRef} className="flex items-center pr-4">
              {worldItems.map((item, i) => (
                <div key={`${item.symbol}-${item.market}`} className="flex items-center">
                  {i > 0 && (
                    <div className="w-px h-5 mx-1 shrink-0" style={{ background: 'var(--glass-border)' }} />
                  )}
                  <WorldIndexStripItem item={item} />
                </div>
              ))}
            </div>
            {marqueeEnabled && (
              <div className="flex items-center pr-4" aria-hidden>
                {worldItems.map((item, i) => (
                  <div key={`dup-${item.symbol}-${item.market}`} className="flex items-center">
                    {i > 0 && (
                      <div className="w-px h-5 mx-1 shrink-0" style={{ background: 'var(--glass-border)' }} />
                    )}
                    <WorldIndexStripItem item={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Separator */}
      <div className="w-px h-4 shrink-0" style={{ background: 'var(--glass-border)' }} />

      {/* Y-axis scale selector */}
      <div ref={scaleRef} className="relative hidden md:flex items-center shrink-0">
        <button
          onClick={() => {
            if (!scaleOpen) {
              const rect = scaleRef.current?.getBoundingClientRect();
              if (rect) {
                setScalePos({
                  bottom: window.innerHeight - rect.top + 4,
                  right: window.innerWidth - rect.right,
                });
              }
            }
            setScaleOpen((v) => !v);
          }}
          title="Price axis scale"
          className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
            priceScaleType !== 'normal'
              ? 'bg-accent/20 text-accent'
              : 'text-muted hover:text-text hover:bg-white/[0.06]'
          }`}
        >
          {SCALE_OPTIONS.find((o) => o.value === priceScaleType)?.label ?? 'Normal'}
        </button>

        {scaleOpen && scalePos && createPortal(
          <div
            className="glass-bar border rounded shadow-lg py-0.5 z-modal-elevated animate-menu-enter"
            style={{
              position: 'fixed',
              bottom: `${scalePos.bottom}px`,
              right: `${scalePos.right}px`,
              minWidth: '90px',
              borderColor: 'var(--glass-border)',
            }}
          >
            {SCALE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                title={opt.title}
                onClick={() => {
                  setPriceScaleType(opt.value);
                  setScaleOpen(false);
                  const type =
                    opt.value === 'log'        ? YAxisType.Log
                    : opt.value === 'percentage' ? YAxisType.Percentage
                    : YAxisType.Normal;
                  chart?.setStyles({ yAxis: { type } } as never);
                  chart?.resize();
                }}
                className={`w-full text-left px-3 py-1 text-[11px] transition-colors ${
                  priceScaleType === opt.value
                    ? 'text-accent bg-accent/10'
                    : 'text-muted hover:text-text hover:bg-white/[0.06]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>

      {/* Market status + IST clock */}
      <div className="hidden md:flex items-center gap-2 pr-1 shrink-0">
        {marketOpen ? (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-green-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
            </span>
            OPEN
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-muted">
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gray-500" />
            CLOSED
          </span>
        )}
        <span className="text-[11px] font-mono text-muted tabular-nums" title="Current time (IST)">
          {timeLabel} IST
        </span>
      </div>
    </div>
  );
}

function formatValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  }
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function formatPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function dayReturnClass(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'text-muted';
  if (value > 0) return 'text-green-400';
  if (value < 0) return 'text-red-400';
  return 'text-muted';
}

function WorldIndexStripItem({ item }: { item: import('@/types').WorldIndexItem }) {
  return (
    <div
      className="flex flex-col justify-center px-3 py-0.5 min-w-[120px] shrink-0"
      title={`${item.market} · ${item.name} · D ${formatPct(item.dayReturnPct)} · W ${formatPct(item.weekReturnPct)}`}
    >
      <div className="flex items-center justify-between text-[10px] leading-none">
        <span className="text-muted">{item.symbol}</span>
        <span className="font-medium text-text">{formatValue(item.value)}</span>
      </div>
      <div className="mt-0.5 flex items-center justify-between text-[9px] leading-none">
        <span className={dayReturnClass(item.dayReturnPct)}>D {formatPct(item.dayReturnPct)}</span>
        <span className={dayReturnClass(item.weekReturnPct)}>W {formatPct(item.weekReturnPct)}</span>
      </div>
    </div>
  );
}


